import { useEffect, useState } from 'preact/hooks';
import { monthName, monthNameSv, monthOf, nextMonth, prevMonth, type MonthKey } from '@kff/engine';
import type { Api } from '../api.js';
import { ApiError } from '../api.js';
import { check, conversion, fromDraft, initialState, toDraft, type FormModel, type FormState } from '../availabilityForm.js';
import { ddm, ddmSv, weekLabel, weekLabelSv, when } from '../format.js';
import { texts, useLang } from '../i18n.js';
import { LoadError, Loading, MonthStepper, PageHead, toast } from '../ui.js';

/** How many months past this one a freelancer can fill in. */
const AHEAD = 5;
const shiftMonth = (m: MonthKey, d: number): MonthKey => {
  let k = m;
  for (let i = 0; i < d; i++) k = nextMonth(k);
  return k;
};
const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);

const TX = texts(
  {
    loading: 'Loading…',
    title: (first: string, month: string) => `Hi ${first} – your availability for ${month}`,
    sub: 'How much you can work each week. The producers plan your work from it.',
    weeksTitle: (month: string) => `Weeks in ${month}`,
    aboutTitle: 'About availability',
    about: 'Give how much you can work each week, as percent of a full-time week or as hours. You can change it any time; the producers always see the latest.',
    month: 'Month',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    updated: (month: string) => `Your availability for ${month} is updated – thank you`,
    sent: (month: string) => `Your availability for ${month} is sent – thank you`,
    undo: 'Undo',
    filled: (month: string) => `Filled in from ${month} – check it and send`,
    submitted: (at: string) => `Sent ${at} – you can change it any time; the producers always see the latest.`,
    notYet: (due: string) => `Not sent yet · please send it by ${due}.`,
    giveAs: 'Give as',
    percent: 'Percent of a full-time week',
    hours: 'Hours',
    percentHint: (base: number) => `100 % = ${base} h a week for you.`,
    hoursHint: 'Hours you can work that week.',
    removeWeekend: 'Remove weekend hours',
    addWeekend: 'Add weekend hours',
    weekendOnHint: 'Hours per weekend day, at most 8 – leave empty if you can’t. The week above is Monday–Friday; weekend hours come on top, and only these days get weekend work.',
    weekendOffHint: 'Optional – only if you can work a Saturday or Sunday. Without it no work is put on your weekends.',
    save: 'Save changes',
    send: 'Send availability',
    same: 'Same as last month',
    nothingIn: (month: string) => `Nothing sent for ${month}.`,
    privacy:
      'What is saved: how much you can work per week and when you sent it. Only producers, the Creative Director and admins at Klara Färdiga Film see it, and it is kept for 12 months after the month ends. The link is personal – don’t share it.'
  },
  {
    loading: 'Laddar…',
    title: (first: string, month: string) => `Hej ${first} – din tillgänglighet för ${month}`,
    sub: 'Hur mycket du kan jobba varje vecka. Producenterna planerar ditt arbete utifrån den.',
    weeksTitle: (month: string) => `Veckor i ${month}`,
    aboutTitle: 'Om tillgänglighet',
    about: 'Ange hur mycket du kan jobba varje vecka, i procent av en heltidsvecka eller i timmar. Du kan ändra den när som helst; producenterna ser alltid den senaste.',
    month: 'Månad',
    prevMonth: 'Föregående månad',
    nextMonth: 'Nästa månad',
    updated: (month: string) => `Tillgängligheten för ${month} är uppdaterad – tack`,
    sent: (month: string) => `Tillgängligheten för ${month} är inskickad – tack`,
    undo: 'Ångra',
    filled: (month: string) => `Ifyllt från ${month} – kolla och skicka`,
    submitted: (at: string) => `Inskickad ${at} – du kan ändra den när som helst; producenterna ser alltid den senaste.`,
    notYet: (due: string) => `Inte inskickad än · skicka den gärna senast ${due}.`,
    giveAs: 'Ange som',
    percent: 'Procent av en heltidsvecka',
    hours: 'Timmar',
    percentHint: (base: number) => `100 % = ${base} h i veckan för dig.`,
    hoursHint: 'Timmar du kan jobba den veckan.',
    removeWeekend: 'Ta bort helgtimmar',
    addWeekend: 'Lägg till helgtimmar',
    weekendOnHint: 'Timmar per helgdag, högst 8 – lämna tomt om du inte kan. Veckan ovan är måndag–fredag; helgtimmarna kommer utöver, och bara de här dagarna får helgarbete.',
    weekendOffHint: 'Valfritt – bara om du kan jobba en lördag eller söndag. Utan det läggs inget arbete på dina helger.',
    save: 'Spara ändringar',
    send: 'Skicka tillgänglighet',
    same: 'Samma som förra månaden',
    nothingIn: (month: string) => `Inget inskickat för ${month}.`,
    privacy:
      'Det här sparas: hur mycket du kan jobba per vecka och när du skickade in det. Det syns bara för producenter, Creative Director och admin på Klara Färdiga Film och sparas i 12 månader efter månadens slut. Länken är personlig – dela den inte.'
  }
);

/**
 * The freelancer's availability page on the personal link (UX-1060/1061) – Swedish by default (du-form), English on the
 * link page's language switch. Percent or hours per week of the coming month, optional weekend hours, "Same as last
 * month" first (most months look like the last one), submit/change, Undo.
 */
export function FreelancerAvailability(p: { api: Api; today: string }) {
  const lang = useLang();
  const t = TX[lang];
  const mon = lang === 'sv' ? monthNameSv : monthName;
  const wk = lang === 'sv' ? weekLabelSv : weekLabel;
  const day = lang === 'sv' ? ddmSv : ddm;
  // this month up to five months ahead, so freelancers can give their availability early (owner 2026-11-02)
  const firstMonth = monthOf(p.today);
  const last = shiftMonth(firstMonth, AHEAD);
  const [picked, setMonth] = useState<MonthKey>(nextMonth(firstMonth));
  const month = picked < firstMonth ? firstMonth : picked > last ? last : picked;
  const [model, setModel] = useState<FormModel | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [err, setErr] = useState<{ message: string; week?: string; day?: string } | null>(null);
  const [fatal, setFatal] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const load = async (m: MonthKey) => {
    try {
      const md = await p.api.get<FormModel>(`/api/availability/form?month=${m}`);
      setModel(md);
      setForm(initialState(md));
      setErr(null);
    } catch (e) {
      setFatal(e);
    }
  };
  useEffect(() => {
    void load(month);
  }, [month]);

  if (fatal) return <LoadError error={fatal} />;
  if (!model || !form) return <Loading label={t.loading} />;
  const m = model;
  const f = form;
  const first = m.name.split(' ')[0] ?? m.name;
  const set = (patch: Partial<FormState>) => setForm({ ...f, ...patch });
  const sv = mon(m.month);

  const submit = async () => {
    const c = check('me', m.month, f, lang);
    if (c.errors.length) {
      const e0 = c.errors[0]!;
      setErr({ message: e0.message, week: e0.week, day: e0.day });
      document.getElementById(e0.week ? `w-${e0.week}` : e0.day ? `d-${e0.day}` : 'av-unit')?.focus();
      return;
    }
    setBusy(true);
    try {
      const r = await p.api.post<{ undoId: string; changed: boolean }>('/api/availability', toDraft(m.month, f));
      await load(m.month);
      toast({
        text: r.changed ? t.updated(sv) : t.sent(sv),
        undoLabel: t.undo,
        undo: async () => {
          await p.api.post(`/api/availability/undo/${r.undoId}`);
          await load(m.month);
        }
      });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'invalid_availability') setErr({ message: e.message });
      else setErr({ message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };
  const same = async () => {
    try {
      const d = await p.api.get<Parameters<typeof fromDraft>[0]>(`/api/availability/form/same-as-last-month?month=${m.month}`);
      setForm(fromDraft(d));
      toast({ text: t.filled(mon(prevMonth(m.month))) });
    } catch (e) {
      setErr({ message: e instanceof Error ? e.message : String(e) });
    }
  };

  return (
    <div class="screen av-page">
      <PageHead title={t.title(first, sv)} sub={t.sub} />
      <div class="mw-cols">
        <section class="blk avform" aria-labelledby="av-h" data-tour="av-weeks">
          <div class="blk-head">
            <h2 class="blk-title" id="av-h">
              {t.weeksTitle(sv)}
            </h2>
            <MonthStepper value={month} min={firstMonth} max={last} label={(k) => `${cap(mon(k))} ${k.slice(0, 4)}`} onChange={setMonth} prevLabel={t.prevMonth} nextLabel={t.nextMonth} groupLabel={t.month} />
          </div>
          <form
            class="avform-body"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            noValidate
          >
            <p class={`av-status${m.submission ? ' tone-ok' : ''}`}>{m.submission ? t.submitted(when(m.submission.submittedAt, lang)) : t.notYet(day(m.dueDay))}</p>
            <div class="av-tools">
              <fieldset class="av-unit" id="av-unit" tabIndex={-1}>
                <legend class="label">{t.giveAs}</legend>
                <div class="cluster">
                  {(
                    [
                      ['percent', t.percent],
                      ['hours', t.hours]
                    ] as const
                  ).map(([v, l]) => (
                    <label class="check">
                      <input type="radio" name="unit" value={v} checked={f.unit === v} onChange={() => set({ unit: v })} /> {l}
                    </label>
                  ))}
                </div>
                <p class="hint">{f.unit === 'percent' ? t.percentHint(m.weeklyBaseH) : t.hoursHint}</p>
              </fieldset>
              <div class="cluster av-same">
                <button type="button" class="btn btn-sm" data-tour={m.lastMonth ? 'av-same' : undefined} disabled={!m.lastMonth} aria-describedby={m.lastMonth ? undefined : 'av-nolast'} onClick={() => void same()}>
                  {t.same}
                </button>
                {!m.lastMonth ? (
                  <span class="hint" id="av-nolast">
                    {t.nothingIn(mon(prevMonth(m.month)))}
                  </span>
                ) : null}
              </div>
            </div>
            <div class="av-rows">
              {m.weeks.map((w) => {
                const wd = m.weekendDays.find((x) => x.week === w)!;
                const invalid = err?.week === w;
                return (
                  <div class="av-row">
                    <label class="av-wk" for={`w-${w}`}>
                      {wk(w)}
                    </label>
                    <span class="av-in">
                      <input
                        class="input"
                        id={`w-${w}`}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        max={f.unit === 'percent' ? 100 : 60}
                        step={1}
                        required
                        aria-invalid={invalid}
                        aria-describedby={invalid ? 'av-err' : undefined}
                        value={f.weeks[w] ?? ''}
                        onInput={(e) =>
                          set({
                            weeks: {
                              ...f.weeks,
                              [w]: (e.target as HTMLInputElement).value
                            }
                          })
                        }
                      />
                      <span class="av-suf" aria-hidden="true">
                        {f.unit === 'percent' ? '%' : 'h'}
                      </span>
                    </span>
                    <span class="av-conv small muted">{conversion(f, w, m.weeklyBaseH)}</span>
                    {f.weekendOn ? (
                      <div class="av-wkrow">
                        {(
                          [
                            [wd.sat, 'lör'],
                            [wd.sun, 'sön']
                          ] as const
                        ).map(([d]) => (
                          <span class="av-wkd">
                            <label for={`d-${d}`}>{day(d)}</label>
                            <span class="av-in">
                              <input
                                class="input"
                                id={`d-${d}`}
                                type="number"
                                inputMode="decimal"
                                min={0}
                                max={8}
                                step={1}
                                placeholder="–"
                                aria-invalid={err?.day === d}
                                value={f.weekend[d] ?? ''}
                                onInput={(e) =>
                                  set({
                                    weekend: {
                                      ...f.weekend,
                                      [d]: (e.target as HTMLInputElement).value
                                    }
                                  })
                                }
                              />
                              <span class="av-suf" aria-hidden="true">
                                h
                              </span>
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div class="av-weekend">
              <button type="button" class="btn btn-ghost btn-sm" onClick={() => set({ weekendOn: !f.weekendOn })}>
                {f.weekendOn ? t.removeWeekend : t.addWeekend}
              </button>
              <p class="hint">{f.weekendOn ? t.weekendOnHint : t.weekendOffHint}</p>
            </div>
            {err ? (
              <p class="field-error" id="av-err" role="alert">
                {err.message}
              </p>
            ) : null}
            <div class="cluster">
              <button type="submit" class="btn btn-primary" data-tour="av-send" disabled={busy}>
                {m.submission ? t.save : t.send}
              </button>
            </div>
          </form>
        </section>
        <aside class="mw-side" aria-label={t.aboutTitle}>
          <section class="blk" aria-labelledby="av-about-h">
            <h2 class="blk-title" id="av-about-h">
              {t.aboutTitle}
            </h2>
            <p class="small">{t.about}</p>
            <p class="small muted privacy">{t.privacy}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}
